import axios, {
  AxiosInstance,
  AxiosResponse,
  AxiosError,
  InternalAxiosRequestConfig,
} from 'axios';
import { getCurrentConfig } from '../config/environment';
import { redirectToLoginPage } from '../routerBasename';

// Extended config type for request tracking
interface ExtendedInternalAxiosRequestConfig
  extends InternalAxiosRequestConfig {
  __requestId?: string;
}

const config = getCurrentConfig();
const API_BASE_URL = config.apiUrl;

// Request tracking for instrumentation
interface RequestMetrics {
  startTime: number;
  url: string;
  method: string;
  isImageRequest: boolean;
}

const requestMetrics = new Map<string, RequestMetrics>();

function isImageRequest(url: string): boolean {
  if (!url) return false;
  const lowerUrl = url.toLowerCase();
  const imageExtensions = [
    '.jpg',
    '.jpeg',
    '.png',
    '.gif',
    '.webp',
    '.svg',
    '.bmp',
  ];
  const imageDomains = ['s3.', 'cloudfront', 'cdn.', 'images.', 'img.'];

  if (imageExtensions.some(ext => lowerUrl.includes(ext))) return true;
  if (imageDomains.some(domain => lowerUrl.includes(domain))) return true;
  if (/\/images?\//.test(lowerUrl) || /\/img\//.test(lowerUrl)) return true;

  return false;
}

function getResponseSize(response: AxiosResponse): number | null {
  const contentLength = response.headers['content-length'];
  if (contentLength) {
    return parseInt(contentLength, 10);
  }

  if (response.data) {
    try {
      return new Blob([JSON.stringify(response.data)]).size;
    } catch {
      return null;
    }
  }

  return null;
}

const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(
  (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    const token =
      typeof window !== 'undefined'
        ? localStorage.getItem('admin_token')
        : null;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    config.headers['Cache-Control'] = 'no-cache';
    config.headers['Pragma'] = 'no-cache';

    const requestId = `${Date.now()}-${Math.random()
      .toString(36)
      .substr(2, 9)}`;
    const fullUrl = config.url ? `${config.baseURL || ''}${config.url}` : '';
    const isImage = isImageRequest(fullUrl);

    requestMetrics.set(requestId, {
      startTime: Date.now(),
      url: fullUrl,
      method: (config.method || 'GET').toUpperCase(),
      isImageRequest: isImage,
    });

    (config as ExtendedInternalAxiosRequestConfig).__requestId = requestId;

    return config;
  },
  (error: AxiosError) => Promise.reject(error)
);

api.interceptors.response.use(
  (response: AxiosResponse) => {
    const requestId = (response.config as ExtendedInternalAxiosRequestConfig)
      .__requestId;
    const metrics = requestId ? requestMetrics.get(requestId) : null;

    if (metrics) {
      const duration = Date.now() - metrics.startTime;
      const responseSize = getResponseSize(response);
      const cacheStatus = response.headers['x-cache'] || 'MISS';
      const cacheControl = response.headers['cache-control'] || '';

      const logEntry = {
        type: 'api_request',
        client: 'admin_app',
        timestamp: new Date().toISOString(),
        method: metrics.method,
        url: metrics.url,
        statusCode: response.status,
        duration: `${duration}ms`,
        durationMs: duration,
        responseSizeBytes: responseSize,
        responseSizeKB: responseSize ? (responseSize / 1024).toFixed(2) : null,
        cacheStatus,
        cacheControl,
        isImageRequest: metrics.isImageRequest,
      };

      if (typeof window !== 'undefined') {
        try {
          const stored = localStorage.getItem('api_request_logs');
          const logs = stored ? JSON.parse(stored) : [];
          logs.push(logEntry);
          if (logs.length > 100) {
            logs.shift();
          }
          localStorage.setItem('api_request_logs', JSON.stringify(logs));
        } catch {
          // Ignore localStorage errors
        }
      }

      if (requestId) {
        requestMetrics.delete(requestId);
      }
    }

    return response;
  },
  (error: AxiosError) => {
    const requestId = error.config
      ? (error.config as ExtendedInternalAxiosRequestConfig).__requestId
      : undefined;
    if (requestId) {
      requestMetrics.delete(requestId);
    }

    if (error.response?.status === 401) {
      const isLoginRequest = error.config?.url?.includes('/login');
      const path =
        typeof window !== 'undefined' ? window.location.pathname : '';
      const isOnLoginPage = path === '/login' || path === '/admin/login';

      if (!isLoginRequest && !isOnLoginPage) {
        if (typeof window !== 'undefined') {
          localStorage.removeItem('admin_token');
          redirectToLoginPage();
        }
      }
    }
    return Promise.reject(error);
  }
);

export default api;
